import AppGrid from "@/features/order-sheet-sets/app-grid";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import { useEffect, useMemo, useState } from "react";
import { AgencyLineSheetSetRow } from "@/features/order-sheet-sets/line-sheet-set-row.type";
import { Spinner, Text } from "@chakra-ui/react";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useLineSheetSetGrid from "@/features/order-sheet-sets/hooks/use-line-sheet-set-grid";
import { LightLineSheetSet } from "@/features/line-sheet-sets/line-sheet-set.types";
import { lineSheetSetMapper } from "@/features/invoices/utils/line-sheet-set-mapper";

interface LineSheetSetGridProps {
  resources?: LightLineSheetSet[];
  mutateResources: () => void;
}

export default function BoutiqueLineSheetSetGrid({
  resources,
  mutateResources,
}: LineSheetSetGridProps) {
  const [rows, setRows] = useState<AgencyLineSheetSetRow[]>();
  const rowMapper = useMemo(() => {
    return lineSheetSetMapper.copy();
  }, []);

  useEffect(() => {
    setRows((prev) => {
      return rowMapper.toRows(resources, prev);
    });
  }, [resources, rowMapper]);

  const { navigateToLineSheetSetPage } = useRouterHelper();
  const { t, tCurrencyString } = useI18nHelper();

  const lineSheetSetGrid = useLineSheetSetGrid({
    rows,
    mutateRows: mutateResources,
  });

  return (
    <>
      <AppGrid
        rows={rows}
        metas={[
          {
            isCheckable: false,
            rowType: "LineSheetSet",
            showCheckAll: false,
            gap: "4px",
            columns: [
              {
                ...lineSheetSetGrid.pairs.lineSheetSetType.column,
                width: "52px",
              },
              { ...lineSheetSetGrid.pairs.season.column, width: "52px" },
              {
                ...lineSheetSetGrid.pairs.brands.column,
                width: "104px",
              },
              {
                ...lineSheetSetGrid.pairs.title.column,
                width: "minmax(224px, 1fr)",
              },
              // 이 부티크에 들어온 진행 중인 오더 — 라인시트세트 상세의 합계와 같은 기준
              {
                name: "주문 통계",
                width: "140px",
                textAlign: "end",
                marginRight: "16px",
              },
              { ...lineSheetSetGrid.pairs.uploadedOn.column, width: "88px" },
              { ...lineSheetSetGrid.pairs.publishedOn.column, width: "88px" },
              { ...lineSheetSetGrid.pairs.validUntil.column, width: "152px" },
            ],
            fields: [
              lineSheetSetGrid.pairs.lineSheetSetType.field,
              lineSheetSetGrid.pairs.season.field,
              lineSheetSetGrid.pairs.brands.field,
              // 주문 금액은 따로 칸으로 뺐으므로 제목 아래에 다시 적지 않는다
              lineSheetSetGrid.pairs.title.field,
              // 주문 금액(굵게) 아래에 총 주문 수 · 주문 수량
              {
                name: "order_statistics",
                // 칸은 -webkit-box 로 그려져 안쪽 Flex 정렬이 먹지 않는다 — 칸 자체를 오른쪽 정렬한다
                chakraProps: { textAlign: "end", marginRight: "16px" },
                value: (_row) => {
                  const row = _row as AgencyLineSheetSetRow;
                  if (!row.orderedOrderCount) {
                    return <Text color={"#8F8F8C"}>-</Text>;
                  }
                  return (
                    <>
                      <Text fontWeight={"700"} whiteSpace={"nowrap"}>
                        {tCurrencyString(row.orderedAmount, {
                          type: "SYMBOL",
                          code: row.currency,
                        })}
                      </Text>
                      <Text
                        fontSize={"11px"}
                        color={"#6F6F6C"}
                        whiteSpace={"nowrap"}
                      >
                        {`${row.orderedOrderCount.toLocaleString()}건 · ${row.orderedQuantity.toLocaleString()}${t("quantity")}`}
                      </Text>
                    </>
                  );
                },
              },
              {
                ...lineSheetSetGrid.pairs.uploadedOn.field,
                chakraProps: { fontWeight: "700" },
              },
              {
                ...lineSheetSetGrid.pairs.publishedOn.field,
                chakraProps: { fontWeight: "700" },
              },
              {
                ...lineSheetSetGrid.pairs.validUntil.field,
                chakraProps: { fontWeight: "700" },
              },
            ],
            actions: [
              lineSheetSetGrid.actions.downloadCompressedImportedFile,
              lineSheetSetGrid.actions.deleteLineSheetSet,
              lineSheetSetGrid.actions.restoreLineSheetSet,
            ],
            indicator: (row) => {
              return (
                <GridRowIndicator
                  type={"VIEW"}
                  isHovered={row.isHovered}
                  isCollapsed={row.isCollapsed}
                />
              );
            },
            onClick: (row) => {
              const lineSheetSetRow = row as AgencyLineSheetSetRow;
              navigateToLineSheetSetPage(lineSheetSetRow.id);
            },
          },
        ]}
      />
    </>
  );
}
